import React from 'react';

// All icons are 24x24 viewBox. Consumers pass a size via prop. `currentColor`
// is used so icons inherit parent color — which lets us keep colors entirely
// theme-driven.

type IconProps = {
  size?: number;
  strokeWidth?: number;
  className?: string;
  title?: string;
};

function Svg({
  size = 20,
  strokeWidth = 2,
  children,
  title,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {children}
    </svg>
  );
}

export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Svg>
);

export const IconBack = (p: IconProps) => (
  <Svg {...p}>
    <polyline points="15 18 9 12 15 6" />
  </Svg>
);

export const IconFile = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </Svg>
);

export const IconMail = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </Svg>
);

export const IconBolt = (p: IconProps) => (
  <Svg {...p}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </Svg>
);

export const IconCheck = (p: IconProps) => (
  <Svg {...p}>
    <polyline points="20 6 9 17 4 12" />
  </Svg>
);

/**
 * Check-in-circle. Used to indicate "successfully connected /
 * verified" states (Gmail-connected banner, post-OTP success). The
 * arc plus checkmark reads as "completed" more than a bare check.
 */
export const IconCheckCircle = (p: IconProps) => (
  <Svg {...p}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </Svg>
);

export const IconAlert = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </Svg>
);

export const IconEye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

export const IconEyeOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.54 20.54 0 0 1 4.5-5.81" />
    <path d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.58" />
    <path d="M22 12s-1.3 2.64-3.76 4.77" />
    <path d="M9.88 4.12A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.32 20.32 0 0 1-1.83 2.82" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </Svg>
);

export const IconShield = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </Svg>
);

export const IconLock = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Svg>
);

export const IconCopy = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Svg>
);

export const IconUpload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </Svg>
);

export const IconInbox = (p: IconProps) => (
  <Svg {...p}>
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </Svg>
);

// Inbox import — Lucide mail-search (envelope with magnifying glass).
// A single purpose-built icon, not a composite. Used for the "scan my
// inbox for CAS emails" flow (OAuth-based).
export const IconInboxScan = (p: IconProps) => (
  <Svg {...p}>
    <path d="M22 12.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h7.5" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    <path d="M18 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    <circle cx="18" cy="18" r="3" />
    <path d="m22 22-1.5-1.5" />
  </Svg>
);

// Forward email — Lucide send-horizontal (paper plane). The universal
// "send/forward email" icon. Visually distinct from IconUpload (up-arrow
// out of box). Used for the "forward your CAS email to a unique address"
// flow.
export const IconForwardEmail = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" />
    <path d="M6 12h16" />
  </Svg>
);

// CAS Generator mailback — Lucide file-output (document with an arrow
// leaving it). Says "generate and output a document". Used for the
// "Email me a fresh CAS" flow (KFintech/CAMS mailback).
export const IconGeneratorMailback = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.226 20.925A2 2 0 0 0 6 22h12a2 2 0 0 0 2-2V8a2.4 2.4 0 0 0-.706-1.706l-3.588-3.588A2.4 2.4 0 0 0 14 2H6a2 2 0 0 0-2 2v3.127" />
    <path d="M14 2v5a1 1 0 0 0 1 1h5" />
    <path d="m5 11-3 3" />
    <path d="m5 17-3-3h10" />
  </Svg>
);

export const IconGoogle = (p: IconProps = {}) => (
  // Colored Google logo — keeps brand colors, ignores currentColor.
  <svg
    width={p.size ?? 18}
    height={p.size ?? 18}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export const IconMicrosoft = (p: IconProps = {}) => (
  // Colored Microsoft logo — four-square, keeps brand colors.
  <svg
    width={p.size ?? 18}
    height={p.size ?? 18}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="9" height="9" fill="#F25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
    <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
    <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
  </svg>
);

export const IconZoho = (p: IconProps = {}) => (
  // Official Zoho logo (mark + wordmark) — brand colors preserved.
  // Source: https://zohowebstatic.com/sites/zweb/images/commonroot/zoho-logo-web.svg
  <svg
    width={p.size ?? 18}
    height={p.size ?? 18}
    viewBox="0 0 1024 450"
    aria-hidden="true"
  >
    {/* Z (green) */}
    <path
      fill="#089949"
      d="M458.1,353c-7.7,0-15.5-1.6-23-4.9l0,0l-160-71.3c-28.6-12.7-41.5-46.4-28.8-75l71.3-160 c12.7-28.6,46.4-41.5,75-28.8l160,71.3c28.6,12.7,41.5,46.4,28.8,75l-71.3,160C500.6,340.5,479.8,353,458.1,353z M448.4,318.1 c12.1,5.4,26.3-0.1,31.7-12.1l71.3-160c5.4-12.1-0.1-26.3-12.1-31.7L379.2,43c-12.1-5.4-26.3,0.1-31.7,12.1l-71.3,160 c-5.4,12.1,0.1,26.3,12.1,31.7L448.4,318.1z"
    />
    {/* O (yellow) */}
    <path
      fill="#F9B21D"
      d="M960,353.1H784.8c-31.3,0-56.8-25.5-56.8-56.8V121.1c0-31.3,25.5-56.8,56.8-56.8H960 c31.3,0,56.8,25.5,56.8,56.8v175.2C1016.8,327.6,991.3,353.1,960,353.1z M784.8,97.1c-13.2,0-24,10.8-24,24v175.2 c0,13.2,10.8,24,24,24H960c13.2,0,24-10.8,24-24V121.1c0-13.2-10.8-24-24-24H784.8z"
    />
    {/* H (red) */}
    <path
      fill="#E42527"
      d="M303.9,153.2L280.3,206c-0.3,0.6-0.6,1.1-0.9,1.6l9.2,56.8c2.1,13.1-6.8,25.4-19.8,27.5l-173,28 c-6.3,1-12.7-0.5-17.9-4.2c-5.2-3.7-8.6-9.3-9.6-15.6l-28-173c-1-6.3,0.5-12.7,4.2-17.9c3.7-5.2,9.3-8.6,15.6-9.6l173-28 c1.3-0.2,2.6-0.3,3.8-0.3c11.5,0,21.8,8.4,23.7,20.2l9.3,57.2L294.3,94l-1.3-7.7c-5-30.9-34.2-52-65.1-47l-173,28 C40,69.6,26.8,77.7,18,90c-8.9,12.3-12.4,27.3-10,42.3l28,173c2.4,15,10.5,28.1,22.8,37C68.5,349.4,80,353,91.9,353 c3,0,6.1-0.2,9.2-0.7l173-28c30.9-5,52-34.2,47-65.1L303.9,153.2z"
    />
    {/* O (blue) */}
    <path
      fill="#226DB4"
      d="M511.4,235.8l25.4-56.9l-7.2-52.9c-0.9-6.3,0.8-12.6,4.7-17.7c3.9-5.1,9.5-8.4,15.9-9.2l173.6-23.6 c1.1-0.1,2.2-0.2,3.3-0.2c5.2,0,10.2,1.7,14.5,4.9c0.8,0.6,1.5,1.3,2.2,1.9c7.7-8.1,17.8-13.9,29.1-16.4c-3.2-4.4-7-8.3-11.5-11.7 c-12.1-9.2-27-13.1-42-11.1L545.6,66.5c-15,2-28.4,9.8-37.5,21.9c-9.2,12.1-13.1,27-11.1,42L511.4,235.8z"
    />
    <path
      fill="#226DB4"
      d="M806.8,265.1l-22.8-168c-12.8,0.4-23.1,11-23.1,23.9v49.3l13.5,99.2c0.9,6.3-0.8,12.6-4.7,17.7 s-9.5,8.4-15.9,9.2l-173.6,23.6c-6.3,0.9-12.6-0.8-17.7-4.7c-5.1-3.9-8.4-9.5-9.2-15.9l-8-58.9l-25.4,56.9l0.9,6.4 c2,15,9.8,28.4,21.9,37.5c10,7.6,21.9,11.6,34.3,11.6c2.6,0,5.2-0.2,7.8-0.5L758.2,329c15-2,28.4-9.8,37.5-21.9 C804.9,295,808.8,280.1,806.8,265.1z"
    />
    {/* Wordmark */}
    <path
      fill="#000"
      d="M317,436.2l24.8-36.6h-20.4c-1.1,0-2-0.9-2-2v-4.9c0-1.1,0.9-2,2-2h33.5c1.1,0,2,0.9,2,2v1.9c0,0.4-0.1,0.8-0.3,1.1 l-24.3,36.6h21.8c1.1,0,2,0.9,2,2v4.9c0,1.1-0.9,2-2,2h-35.4c-1.1,0-2-0.9-2-2v-1.8C316.6,436.9,316.8,436.5,317,436.2z"
    />
    <path
      fill="#000"
      d="M421.4,415.6c0-15,11-25.8,26-25.8c15.5,0,26,10.6,26,25.9c0,15.5-10.7,26.2-26.2,26.2 C431.6,441.9,421.4,431.2,421.4,415.6z M461.5,415.8c0-9.1-4.4-16.9-14.3-16.9c-10,0-13.8,8.1-13.8,17.3c0,8.7,4.7,16.7,14.3,16.7 C457.6,432.8,461.5,424.3,461.5,415.8z"
    />
    <path
      fill="#000"
      d="M544.5,390.6h7.4c1.1,0,2,0.9,2,2v18.6h21v-18.6c0-1.1,0.9-2,2-2h7.4c1.1,0,2,0.9,2,2v46.5c0,1.1-0.9,2-2,2H577 c-1.1,0-2-0.9-2-2v-18.8h-21v18.8c0,1.1-0.9,2-2,2h-7.4c-1.1,0-2-0.9-2-2v-46.5C542.5,391.5,543.4,390.6,544.5,390.6z"
    />
    <path
      fill="#000"
      d="M655.4,415.6c0-15,11-25.8,26-25.8c15.5,0,26,10.6,26,25.9c0,15.5-10.7,26.2-26.2,26.2 C665.6,441.9,655.4,431.2,655.4,415.6z M695.4,415.8c0-9.1-4.4-16.9-14.3-16.9c-10,0-13.8,8.1-13.8,17.3c0,8.7,4.7,16.7,14.3,16.7 C691.5,432.8,695.4,424.3,695.4,415.8z"
    />
  </svg>
);

export const IconChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <polyline points="9 18 15 12 9 6" />
  </Svg>
);

export const IconPieChart = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </Svg>
);

export const IconLayers = (p: IconProps) => (
  <Svg {...p}>
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </Svg>
);
